import { useEffect, useState } from 'react';
import { Eye, EyeOff, Lock, Mail, Phone, User, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const INITIAL_REGISTER_FORM = {
  name: '',
  lastName: '',
  email: '',
  password: '',
  phoneNumber: '',
  address: '',
  city: '',
  zipCode: '',
};

export default function AuthModal() {
  const { authMode, closeAuthModal, isAuthModalOpen, login, register } = useAuth();

  const [mode, setMode] = useState(authMode);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState(INITIAL_REGISTER_FORM);

  useEffect(() => {
    if (isAuthModalOpen) {
      setMode(authMode);
      setFeedback(null);
    }
  }, [authMode, isAuthModalOpen]);

  if (!isAuthModalOpen) {
    return null;
  }

  async function submit(action) {
    setIsSubmitting(true);
    setFeedback(null);
    try {
      await action();
    } catch (error) {
      setFeedback({ type: 'error', message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleLoginSubmit(event) {
    event.preventDefault();
    submit(() => login(loginForm));
  }

  function handleRegisterSubmit(event) {
    event.preventDefault();
    submit(() => register(registerForm));
  }

  function updateRegister(field) {
    return (event) => setRegisterForm((current) => ({ ...current, [field]: event.target.value }));
  }

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-label="Acceso OGC">
      <button className="modal-backdrop" onClick={closeAuthModal} aria-label="Cerrar acceso" />
      <section className="auth-modal">
        <div className="auth-modal__header">
          <div className="brand-mark brand-mark--large">
            <img src="/logo.png" alt="OG Canna" />
          </div>
          <button className="icon-button" onClick={closeAuthModal} aria-label="Cerrar modal">
            <X size={18} />
          </button>
          <p className="eyebrow">Acceso</p>
          <h2>OG Canna</h2>
        </div>

        <div className="auth-tabs">
          <button className={mode === 'login' ? 'is-active' : ''} onClick={() => setMode('login')}>
            Iniciar sesión
          </button>
          <button className={mode === 'register' ? 'is-active' : ''} onClick={() => setMode('register')}>
            Registrarse
          </button>
        </div>

        {feedback && <div className={`auth-feedback auth-feedback--${feedback.type}`}>{feedback.message}</div>}

        {mode === 'login' && (
          <form className="auth-form" onSubmit={handleLoginSubmit}>
            <label className="field">
              <span>Correo electrónico</span>
              <div className="field-control">
                <Mail size={16} />
                <input
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(event) => setLoginForm((current) => ({ ...current, email: event.target.value }))}
                  placeholder="tu@correo.com"
                />
              </div>
            </label>

            <label className="field">
              <span>Contraseña</span>
              <div className="field-control">
                <Lock size={16} />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  required
                  value={loginForm.password}
                  onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))}
                />
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => setIsPasswordVisible((current) => !current)}
                  aria-label="Mostrar contraseña"
                >
                  {isPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>

            <button className="button button--primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}

        {mode === 'register' && (
          <form className="auth-form" onSubmit={handleRegisterSubmit}>
            <div className="field-grid field-grid--two">
              <label className="field">
                <span>Nombre</span>
                <div className="field-control">
                  <User size={16} />
                  <input value={registerForm.name} onChange={updateRegister('name')} required />
                </div>
              </label>
              <label className="field">
                <span>Apellidos</span>
                <div className="field-control">
                  <User size={16} />
                  <input value={registerForm.lastName} onChange={updateRegister('lastName')} required />
                </div>
              </label>
            </div>

            <div className="field-grid field-grid--two">
              <label className="field">
                <span>Email</span>
                <div className="field-control">
                  <Mail size={16} />
                  <input type="email" value={registerForm.email} onChange={updateRegister('email')} required />
                </div>
              </label>
              <label className="field">
                <span>Teléfono</span>
                <div className="field-control">
                  <Phone size={16} />
                  <input value={registerForm.phoneNumber} onChange={updateRegister('phoneNumber')} required />
                </div>
              </label>
            </div>

            <label className="field">
              <span>Contraseña</span>
              <div className="field-control">
                <Lock size={16} />
                <input
                  type={isPasswordVisible ? 'text' : 'password'}
                  value={registerForm.password}
                  onChange={updateRegister('password')}
                  required
                  minLength={8}
                />
                <button
                  className="icon-button"
                  type="button"
                  onClick={() => setIsPasswordVisible((current) => !current)}
                  aria-label="Mostrar contraseña"
                >
                  {isPasswordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </label>
            <p className="muted-copy">Mínimo 8 caracteres, con mayúscula, minúscula y número.</p>

            <button className="button button--primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creando cuenta...' : 'Crear cuenta'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
